import { useState, useEffect } from 'react';
import { History, RefreshCw, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import api from '../../lib/api';
import { toast } from 'sonner';

const ACTION_LABELS = {
  create_admin: { label: 'Criar Admin', color: 'bg-indigo-100 text-indigo-800' },
  promote_to_admin: { label: 'Promover a Admin', color: 'bg-indigo-100 text-indigo-800' },
  demote_admin: { label: 'Rebaixar Admin', color: 'bg-slate-100 text-slate-800' },
  block_user: { label: 'Bloquear', color: 'bg-amber-100 text-amber-800' },
  unblock_user: { label: 'Desbloquear', color: 'bg-emerald-100 text-emerald-800' },
  reset_password: { label: 'Reset Senha', color: 'bg-orange-100 text-orange-800' },
  delete_user: { label: 'Excluir', color: 'bg-red-100 text-red-800' },
};

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?action=${filter}` : '';
      const { data } = await api.get(`/admin/audit-logs${params}`);
      setLogs(data.items || []);
    } catch (e) {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminLayout title="Logs de Ações Administrativas">
      <Card className="rounded-2xl">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <History className="w-5 h-5" />
                {logs.length} registro{logs.length !== 1 ? 's' : ''}
              </CardTitle>
              <CardDescription>
                Trilha de auditoria de todas as ações administrativas realizadas no sistema
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-48" data-testid="audit-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as ações</SelectItem>
                  {Object.entries(ACTION_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={load} data-testid="audit-reload">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b text-xs uppercase text-slate-500">
                    <th className="py-2 px-3">Data/Hora</th>
                    <th className="py-2 px-3">Admin</th>
                    <th className="py-2 px-3">Ação</th>
                    <th className="py-2 px-3">Alvo</th>
                    <th className="py-2 px-3">IP</th>
                  </tr>
                </thead>
                <tbody data-testid="audit-logs-list">
                  {logs.map((l) => {
                    const cfg = ACTION_LABELS[l.action] || { label: l.action, color: 'bg-slate-100' };
                    return (
                      <tr key={l.id} className="border-b hover:bg-slate-50">
                        <td className="py-2.5 px-3 text-xs text-slate-600 whitespace-nowrap">
                          {new Date(l.created_at).toLocaleString('pt-BR')}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="text-xs font-medium text-slate-800">{l.admin_name || '-'}</div>
                          <div className="text-[10px] text-slate-500">{l.admin_email}</div>
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={`${cfg.color} hover:${cfg.color}`}>{cfg.label}</Badge>
                        </td>
                        <td className="py-2.5 px-3 text-xs text-slate-600">
                          {l.target_email || '-'}
                          {l.meta?.reason && (
                            <div className="text-[10px] text-amber-700">Motivo: {l.meta.reason}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-[10px] text-slate-400 font-mono">{l.ip || '-'}</td>
                      </tr>
                    );
                  })}
                  {logs.length === 0 && (
                    <tr><td colSpan="5" className="text-center py-8 text-slate-400">Sem registros</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminAuditLogs;
